// Conversão e formatação dos valores de multa/juros.
//
// O contrato guarda `amount` como INTEIRO com 2 casas implícitas (centavos no
// MONETARY, centésimos de % no PERCENTAGE). O usuário digita "10,50" — e
// `parseFloat("10.50") * 100` dá 1049.9999999999998. Daí a conversão por
// STRING: separa inteiro e fração e soma, sem passar por ponto flutuante.
import type {
  PaymentConfigurationAmountType,
  PaymentConfigurationCharge,
} from "@/types/payment-configurations"

// Inteiro + até 2 casas, já normalizado para ponto decimal.
const NORMALIZED_DECIMAL = /^(\d+)(?:\.(\d{1,2}))?$/
const INTEGER = /^\d+$/
// Agrupamento de milhar VÁLIDO: 1.234 / 12.345.678 (blocos de 3, sem zero à
// esquerda — `0.125` é decimal mal digitado, não 125).
const THOUSANDS = /^[1-9]\d{0,2}(?:\.\d{3})+$/
// Ponto decimal (sem vírgula): 10.5 / 10.50.
const DOT_DECIMAL = /^\d+\.\d{1,2}$/

/**
 * Normaliza o texto digitado para `1234.56`, aceitando os formatos de quem
 * digita em pt-BR e en — ou `null` quando é ambíguo/inválido:
 * - com vírgula: a vírgula é o decimal e os pontos, se houver, precisam ser
 *   milhar bem formado (`1.234,56`);
 * - sem vírgula: `10.5` é decimal, `1.234` é milhar; qualquer outro ponto
 *   (`0.001`, `1.2345`, `12.34.56`) é recusado — interpretar errado viraria
 *   valor 100× maior numa configuração que não pode ser editada.
 */
const normalizeDecimalInput = (
  raw: string,
  allowThousands: boolean
): string | null => {
  const compact = raw.replace(/[\sR$%]/g, "")
  if (compact.includes(",")) {
    const [integerPart, ...rest] = compact.split(",")
    if (rest.length !== 1) return null
    const groupedOk =
      !integerPart.includes(".") ||
      (allowThousands && THOUSANDS.test(integerPart))
    return groupedOk
      ? `${integerPart.replace(/\./g, "")}.${rest[0]}`
      : null
  }
  if (!compact.includes(".")) return compact
  if (DOT_DECIMAL.test(compact)) return compact
  if (allowThousands && THOUSANDS.test(compact)) {
    return compact.replace(/\./g, "")
  }
  return null
}

/**
 * Texto em reais/percentual → inteiro com 2 casas implícitas.
 * `"10,50"` → `1050`, `"2,5"` → `250`, `"0,1"` → `10`.
 *
 * `null` quando o texto não é um número válido ou tem mais de 2 casas — o
 * schema transforma isso em erro em vez de arredondar calado.
 */
export const parseDecimalToHundredths = (
  raw: string,
  {
    allowThousands = true,
  }: { allowThousands?: boolean } = {}
): number | null => {
  const normalized = normalizeDecimalInput(
    raw.trim(),
    allowThousands
  )
  if (normalized === null) return null
  const match = NORMALIZED_DECIMAL.exec(normalized)
  if (!match) return null
  const [, integerPart, fractionPart = ""] = match
  const value =
    Number(integerPart) * 100 +
    Number(fractionPart.padEnd(2, "0"))
  return Number.isSafeInteger(value) ? value : null
}

/** Teto do percentual: 100,00% = 10000 centésimos de ponto percentual. */
export const PERCENTAGE_MAX_HUNDREDTHS = 10_000

/**
 * Valor de multa/juros conforme o tipo. Percentual NÃO aceita separador de
 * milhar: `1.234%` não é uma regra de cobrança plausível, é digitação errada.
 */
export const parseChargeAmount = (
  raw: string,
  type: PaymentConfigurationAmountType
): number | null =>
  parseDecimalToHundredths(raw, {
    allowThousands: type === "MONETARY",
  })

/** Texto de dias → inteiro ≥ 0, ou `null` se não for inteiro. */
export const parseNonNegativeInteger = (
  raw: string
): number | null => {
  const trimmed = raw.trim()
  if (!INTEGER.test(trimmed)) return null
  const value = Number(trimmed)
  return Number.isSafeInteger(value) ? value : null
}

const HUNDREDTHS = 100
// PERCENTAGE: 200 = 2,00% = 0,02 → divide por 100 (casas) e por 100 (%).
const PERCENT_DIVISOR = 10_000

/**
 * `amount` do contrato → texto localizado.
 * MONETARY: `1050` → `R$ 10,50`. PERCENTAGE: `200` → `2,00%`.
 */
export const formatChargeAmount = (
  amount: number,
  type: PaymentConfigurationAmountType,
  locale: string
): string =>
  type === "MONETARY"
    ? new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "BRL",
      }).format(amount / HUNDREDTHS)
    : new Intl.NumberFormat(locale, {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount / PERCENT_DIVISOR)

/** Multa/juros formatados, ou `null` quando a regra não existe. */
export const formatCharge = (
  charge: PaymentConfigurationCharge | undefined,
  locale: string
): string | null =>
  charge
    ? formatChargeAmount(charge.amount, charge.type, locale)
    : null

/** Data ISO do core (`2026-10-29T17:00:00`) → data/hora localizada. */
export const formatDateTime = (
  value: string,
  locale: string
): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date)
}
